/**
 * Thread routing: club-scoped URL when club_id + slug exist; otherwise canonical `/thread/:id`.
 * Community and club thread routes use `ClubPostThreadPage` (including `/community/thread/:id`).
 */

/** @typedef {'community' | 'club'} ThreadNavOrigin */

export const THREAD_NAV_ORIGIN_COMMUNITY = "community";
export const THREAD_NAV_ORIGIN_CLUB = "club";

function pickClubId(thread) {
  const n = Number(thread?.clubId ?? thread?.club_id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function pickClubSlug(thread) {
  const s = String(thread?.clubSlug ?? thread?.club_slug ?? "").trim();
  return s || null;
}

/**
 * Normalize API/activity rows into a minimal `{ id, club_* }` shape for routing.
 * Prefers `post_id` / `postId` when present so forum_comment rows open the thread, not the comment id.
 * @param {object|null|undefined} row
 * @returns {{ id: string, clubId?: number, club_id?: number, clubSlug?: string, club_slug?: string } | null}
 */
export function threadNavShapeFromLoose(row) {
  if (!row || typeof row !== "object") return null;
  const postId = row.post_id ?? row.postId;
  const fallbackId = row.id ?? row.threadId ?? row._id;
  const rawId =
    postId != null && String(postId).trim() !== "" ? postId : fallbackId != null ? fallbackId : null;
  if (rawId == null || String(rawId).trim() === "") return null;
  const id = String(rawId);
  const clubIdRaw = row.clubId ?? row.club_id;
  const clubIdNum = Number(clubIdRaw);
  const slugRaw = row.clubSlug ?? row.club_slug;
  const clubSlug = slugRaw != null && String(slugRaw).trim() !== "" ? String(slugRaw).trim() : null;
  /** @type {{ id: string, clubId?: number, club_id?: number, clubSlug?: string, club_slug?: string }} */
  const out = { id };
  if (Number.isFinite(clubIdNum) && clubIdNum > 0) {
    out.clubId = clubIdNum;
    out.club_id = clubIdNum;
  }
  if (clubSlug) {
    out.clubSlug = clubSlug;
    out.club_slug = clubSlug;
  }
  return out;
}

/**
 * Pathname only (leading `/`). Club URL only when both club id and slug resolve; otherwise `/thread/:id`.
 * @param {object|null|undefined} thread — normalized feed post, API row, or loose activity row
 * @returns {string}
 */
export function getCanonicalForumThreadPath(thread) {
  const shape = threadNavShapeFromLoose(thread);
  if (!shape?.id) return "/";
  const clubId = pickClubId(shape);
  const clubSlug = pickClubSlug(shape);
  if (clubId != null && clubSlug) {
    return `/clubs/${encodeURIComponent(clubSlug)}/posts/${encodeURIComponent(String(shape.id))}`;
  }
  return `/thread/${encodeURIComponent(String(shape.id))}`;
}

/**
 * Full shareable URL for the current origin.
 * @param {object|null|undefined} thread
 * @returns {string}
 */
export function getCanonicalForumThreadAbsoluteUrl(thread) {
  const path = getCanonicalForumThreadPath(thread);
  const origin =
    typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";
  if (!path || path === "/") return origin || "";
  return origin ? `${origin}${path}` : path;
}

/**
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {object} thread — feed / nav state (id or post_id; optional clubId, club_id, clubSlug, club_slug)
 * @param {{ origin?: ThreadNavOrigin, extraState?: object }} [opts]
 */
export function navigateToCanonicalForumThread(navigate, thread, opts = {}) {
  const shape = threadNavShapeFromLoose(thread);
  if (!shape?.id) return;
  const path = getCanonicalForumThreadPath(shape);
  if (!path || path === "/") return;
  const origin =
    opts.origin === THREAD_NAV_ORIGIN_CLUB ? THREAD_NAV_ORIGIN_CLUB : THREAD_NAV_ORIGIN_COMMUNITY;
  const extra = opts.extraState && typeof opts.extraState === "object" ? opts.extraState : {};
  const stateThread =
    thread && typeof thread === "object" ? { ...thread, ...shape } : shape;

  navigate(path, {
    state: {
      thread: stateThread,
      threadOrigin: origin,
      ...extra,
    },
  });
}
